import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AffectedUnit, IncidentSeverity, Recall, RecallStatus, RecallSummary } from '@regigov/contracts';
import { recallsApi } from '../api/selfservice';
import { useAuth } from '../auth/AuthContext';
import { isCerRole } from '../auth/roles';
import { DefinitionList, Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

type Mode = 'cer' | 'participant';
type Paths = { mode: Mode; basePath: string; devicesPath: string; incidentsPath: string };
const SEVERITIES: IncidentSeverity[] = ['low', 'medium', 'high', 'critical'];

export function RecallsPage({ mode, basePath }: Paths) {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const q = params.get('q') ?? '';
  const [draft, setDraft] = useState(q);
  const query = { status: (status || undefined) as RecallStatus | undefined, q: q || undefined };
  const list = useQuery({ queryKey: ['recalls', query], queryFn: () => recallsApi.list(query) });

  function apply(next: Record<string, string>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p);
  }

  return (
    <>
      <div className="row">
        <h1>Recalls</h1>
        <span className="grow" />
        {mode === 'cer' && (
          <Link className="button" to={`${basePath}/new`}>
            Raise a recall
          </Link>
        )}
      </div>
      <p className="muted">
        {mode === 'cer'
          ? 'Recalls raised against a device model, optionally scoped to batches. Each affected unit is snapshotted at recall time and tracked to resolution; a recall closes once every unit is closed.'
          : 'Recalls affecting models you manufacture or units you own or installed. Acknowledge and remediate the units you are responsible for; CER verifies and closes them.'}
      </p>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          apply({ q: draft });
        }}
      >
        <input className="grow" placeholder="Search by title, id or model number" value={draft} onChange={(e) => setDraft(e.target.value)} />
        <select value={status} onChange={(e) => apply({ status: e.target.value })} aria-label="Status filter">
          <option value="">All statuses</option>
          <option value="open">open</option>
          <option value="closed">closed</option>
        </select>
        <button type="submit">Search</button>
      </form>
      <ProblemAlert error={list.error} />
      {list.data && <RecallsTable items={list.data.items} basePath={basePath} />}
    </>
  );
}

function RecallsTable({ items, basePath }: { items: RecallSummary[]; basePath: string }) {
  if (items.length === 0) return <p className="muted">No recalls match.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Recall</th>
          <th>Severity</th>
          <th>Status</th>
          <th>Model</th>
          <th>Scope</th>
          <th>Units (closed / total)</th>
          <th>Raised</th>
        </tr>
      </thead>
      <tbody>
        {items.map((r) => (
          <tr key={r.id}>
            <td>
              <Link to={`${basePath}/${r.id}`}>{r.title}</Link>
              <br />
              <code className="muted">{r.id}</code>
            </td>
            <td>
              <StatusBadge status={r.severity} />
            </td>
            <td>
              <StatusBadge status={r.status} />
            </td>
            <td>
              {r.modelNumber} <span className="muted">{r.manufacturerName}</span> <StatusBadge status={r.modelStatus} />
            </td>
            <td>{r.batches ? `Batches ${r.batches.join(', ')}` : <span className="muted">all units</span>}</td>
            <td>
              {r.counts.closed} / {r.counts.total}
            </td>
            <td>{formatDateTime(r.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function NewRecallPage({ basePath, devicesPath, incidentsPath }: Paths) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [values, setValues] = useState({
    modelId: params.get('modelId') ?? '',
    batches: params.get('batch') ?? '',
    incidentId: params.get('incidentId') ?? '',
    severity: 'high' as IncidentSeverity,
    title: '',
    description: '',
    remedy: '',
    withdrawModel: false,
  });
  const raise = useMutation({
    mutationFn: () => {
      const batches = values.batches
        .split(',')
        .map((b) => b.trim())
        .filter(Boolean);
      return recallsApi.raise({
        modelId: values.modelId.trim(),
        ...(batches.length > 0 && { batches }),
        ...(values.incidentId && { incidentId: values.incidentId.trim() }),
        severity: values.severity,
        title: values.title,
        description: values.description,
        remedy: values.remedy,
        withdrawModel: values.withdrawModel,
      });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['recalls'] });
      void qc.invalidateQueries({ queryKey: ['incidents'] });
      void qc.invalidateQueries({ queryKey: ['models'] });
      navigate(`${basePath}/${r.id}`);
    },
  });
  const set = (k: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [k]: e.target.value }));

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    raise.mutate();
  }

  return (
    <>
      <p className="crumbs">
        <Link to={basePath}>Recalls</Link> / Raise
      </p>
      <h1>Raise a recall</h1>
      <p className="muted">
        Identify the model (copy its ID from the <Link to={devicesPath}>register</Link>) and optionally the batches in scope. Every registered or installed unit matching the scope is captured with its
        installation, owner and installer as at this moment, and the installer (or owner, if uninstalled) becomes responsible for remediation.
      </p>
      <form className="form card" onSubmit={onSubmit} noValidate>
        <Field label="Model ID" hint="CER-M-…">
          <input value={values.modelId} onChange={set('modelId')} required disabled={raise.isPending} />
        </Field>
        <Field label="Batches" hint="Comma-separated batch codes; leave blank to recall every unit of the model">
          <input value={values.batches} onChange={set('batches')} disabled={raise.isPending} />
        </Field>
        <Field label="Linked incident" hint="CER-N-…; optional">
          <input value={values.incidentId} onChange={set('incidentId')} disabled={raise.isPending} />
          {values.incidentId && (
            <small>
              <Link to={`${incidentsPath}/${values.incidentId}`}>View incident</Link>
            </small>
          )}
        </Field>
        <Field label="Severity">
          <select value={values.severity} onChange={set('severity')} disabled={raise.isPending}>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Title">
          <input value={values.title} onChange={set('title')} required disabled={raise.isPending} />
        </Field>
        <Field label="Hazard description" hint="What is wrong and the safety impact (at least 10 characters)">
          <textarea rows={4} value={values.description} onChange={set('description')} required disabled={raise.isPending} />
        </Field>
        <Field label="Remedy" hint="What the responsible participant must do for each unit">
          <textarea rows={3} value={values.remedy} onChange={set('remedy')} required disabled={raise.isPending} />
        </Field>
        <Field label="Model status" hint="Withdrawing prevents new units of this model being registered; the change is recorded in the model's history">
          <label>
            <input type="checkbox" checked={values.withdrawModel} onChange={(e) => setValues((v) => ({ ...v, withdrawModel: e.target.checked }))} disabled={raise.isPending} /> Withdraw the model
            from the register as part of this recall
          </label>
        </Field>
        <ProblemAlert error={raise.error} />
        <div className="row">
          <button type="submit" disabled={raise.isPending}>
            Raise recall
          </button>
          <Link to={basePath}>Cancel</Link>
        </div>
      </form>
    </>
  );
}

export function RecallDetailPage({ mode, basePath, devicesPath, incidentsPath }: Paths) {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const recall = useQuery({ queryKey: ['recalls', 'detail', id], queryFn: () => recallsApi.get(id) });
  const [note, setNote] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const onSaved = (r: Recall) => {
    qc.setQueryData(['recalls', 'detail', id], r);
    void qc.invalidateQueries({ queryKey: ['recalls'] });
  };
  const addNote = useMutation({
    mutationFn: () => recallsApi.addNote(id, { note }),
    onSuccess: (r) => {
      setNote('');
      onSaved(r);
    },
  });
  const refresh = useMutation({ mutationFn: () => recallsApi.refresh(id), onSuccess: onSaved });
  const close = useMutation({
    mutationFn: () => recallsApi.close(id, { note: closeNote }),
    onSuccess: (r) => {
      setCloseNote('');
      onSaved(r);
    },
  });

  if (recall.isLoading) return <p className="muted">Loading…</p>;
  if (recall.error || !recall.data) return <ProblemAlert error={recall.error ?? new Error('Not found')} />;
  const r = recall.data;
  const isCerOfficer = mode === 'cer' && isCerRole(user?.roles);
  const canManage = isCerOfficer && r.status !== 'closed';
  const allClosed = r.counts.total === r.counts.closed;

  return (
    <>
      <p className="crumbs">
        <Link to={basePath}>Recalls</Link> / {r.id}
      </p>
      <div className="row">
        <h1>{r.title}</h1>
        <StatusBadge status={r.severity} />
        <StatusBadge status={r.status} />
      </div>
      <section className="card">
        <DefinitionList
          items={[
            ['Recall ID', <code>{r.id}</code>],
            [
              'Model',
              <>
                <Link to={`${devicesPath}/models/${r.modelId}`}>{r.modelNumber}</Link> <StatusBadge status={r.modelStatus} />
              </>,
            ],
            ['Manufacturer', mode === 'cer' ? <Link to={`/cer/participants/${r.manufacturerId}`}>{r.manufacturerName}</Link> : r.manufacturerName],
            ['Scope', r.batches ? `Batches ${r.batches.join(', ')}` : 'All units of the model'],
            ['Linked incident', r.incidentId ? <Link to={`${incidentsPath}/${r.incidentId}`}>{r.incidentId}</Link> : null],
            ['Raised by', r.raisedByUserName ?? r.raisedByUserId],
            ['Raised', formatDateTime(r.createdAt)],
            ['Closed', r.closedAt ? formatDateTime(r.closedAt) : null],
            [
              'Affected units',
              `${r.counts.total} total · ${r.counts.notified} notified · ${r.counts.acknowledged} acknowledged · ${r.counts.remediated} remediated · ${r.counts.verified} verified · ${r.counts.closed} closed`,
            ],
          ]}
        />
        <h3>Hazard</h3>
        <p style={{ whiteSpace: 'pre-wrap' }}>{r.description}</p>
        <h3>Remedy</h3>
        <p style={{ whiteSpace: 'pre-wrap' }}>{r.remedy}</p>
      </section>

      <div className="row">
        <h2>Affected units</h2>
        <span className="grow" />
        {canManage && (
          <button type="button" onClick={() => refresh.mutate()} disabled={refresh.isPending} title="Add units registered against this model/batch since the recall was raised">
            Re-resolve units
          </button>
        )}
      </div>
      <ProblemAlert error={refresh.error} />
      {r.affectedUnits.length === 0 ? (
        <p className="muted">No units matched the recall scope.</p>
      ) : (
        r.affectedUnits.map((u) => (
          <AffectedUnitCard key={u.id} recall={r} unit={u} mode={mode} devicesPath={devicesPath} isCerOfficer={isCerOfficer} participantId={user?.participantId ?? null} onSaved={onSaved} />
        ))
      )}

      <h2>Recall timeline</h2>
      <table className="table">
        <thead>
          <tr>
            <th>When</th>
            <th>Event</th>
            <th>By</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {r.events.map((e) => (
            <tr key={e.id}>
              <td>{formatDateTime(e.createdAt)}</td>
              <td>{e.kind === 'status' ? `${e.fromStatus} → ${e.toStatus}` : e.kind.replace('_', ' ')}</td>
              <td>{e.actorName ?? '—'}</td>
              <td style={{ whiteSpace: 'pre-wrap' }}>{e.note}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {r.status !== 'closed' && (
        <form
          className="form card"
          onSubmit={(e) => {
            e.preventDefault();
            addNote.mutate();
          }}
        >
          <h2>Add a note</h2>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} required disabled={addNote.isPending} />
          <ProblemAlert error={addNote.error} />
          <div>
            <button type="submit" disabled={addNote.isPending || note.trim().length === 0}>
              Add note
            </button>
          </div>
        </form>
      )}

      {canManage && (
        <form
          className="form card"
          onSubmit={(e) => {
            e.preventDefault();
            close.mutate();
          }}
        >
          <h2>Close recall</h2>
          <p className="muted">{allClosed ? 'Every affected unit is closed; the recall can be closed.' : `${r.counts.total - r.counts.closed} unit(s) are still open. Close each unit first.`}</p>
          <Field label="Closing note">
            <textarea rows={2} value={closeNote} onChange={(e) => setCloseNote(e.target.value)} required disabled={close.isPending || !allClosed} />
          </Field>
          <ProblemAlert error={close.error} />
          <div>
            <button type="submit" disabled={close.isPending || !allClosed}>
              Close recall
            </button>
          </div>
        </form>
      )}
    </>
  );
}

const PARTICIPANT_NEXT: Record<string, 'acknowledged' | 'remediated' | null> = { notified: 'acknowledged', acknowledged: 'remediated' };

function AffectedUnitCard({
  recall,
  unit: u,
  mode,
  devicesPath,
  isCerOfficer,
  participantId,
  onSaved,
}: {
  recall: Recall;
  unit: AffectedUnit;
  mode: Mode;
  devicesPath: string;
  isCerOfficer: boolean;
  participantId: string | null;
  onSaved: (r: Recall) => void;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [cerStatus, setCerStatus] = useState<'verified' | 'closed'>(u.status === 'verified' ? 'closed' : 'verified');
  const isResponsible = mode === 'participant' && participantId === u.responsibleParticipantId;
  const participantNext = isResponsible && recall.status === 'open' ? PARTICIPANT_NEXT[u.status] ?? null : null;
  const cerCanAct = isCerOfficer && recall.status === 'open' && u.status !== 'closed';
  const canNote = recall.status === 'open' && u.status !== 'closed' && (isCerOfficer || isResponsible);

  const change = useMutation({
    mutationFn: (status: 'acknowledged' | 'remediated' | 'verified' | 'closed') =>
      recallsApi.changeUnitStatus(recall.id, u.unitId, { status, note, ...(evidenceRef && { evidenceRef }) }),
    onSuccess: (r) => {
      setNote('');
      setEvidenceRef('');
      onSaved(r);
    },
  });
  const addNote = useMutation({
    mutationFn: () => recallsApi.addUnitNote(recall.id, u.unitId, { note, ...(evidenceRef && { evidenceRef }) }),
    onSuccess: (r) => {
      setNote('');
      setEvidenceRef('');
      onSaved(r);
    },
  });

  return (
    <section className="card">
      <div className="row">
        <strong>
          <Link to={`${devicesPath}/units/${u.unitId}`}>{u.serial}</Link>
        </strong>
        {u.batch && <span className="muted">batch {u.batch}</span>}
        <StatusBadge status={u.status} />
        <span className="grow" />
        <span className="muted">
          {u.installationId ? `NMI ${u.nmi ?? '—'}` : `${u.unitStatusAtRecall} (not installed)`} · responsible: {u.responsibleParticipantName}
        </span>
        <button type="button" onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide' : 'Details'}
        </button>
      </div>
      {open && (
        <>
          <DefinitionList
            items={[
              ['Unit ID', <code>{u.unitId}</code>],
              ['Status at recall', u.unitStatusAtRecall],
              ['Installation', u.installationId ? <Link to={`${devicesPath}/installations/${u.installationId}`}>NMI {u.nmi}</Link> : null],
              ['Address', u.address ? `${u.address.line1}, ${u.address.suburb} ${u.address.state} ${u.address.postcode}` : null],
              ['Owner', u.ownerParticipantName],
              ['Installer', u.installerParticipantName],
              ['Responsible', u.responsibleParticipantName],
              ['Added to recall', formatDateTime(u.addedAt)],
            ]}
          />
          <h4>Resolution trail</h4>
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Event</th>
                <th>By</th>
                <th>Note</th>
                <th>Evidence</th>
              </tr>
            </thead>
            <tbody>
              {u.events.map((e) => (
                <tr key={e.id}>
                  <td>{formatDateTime(e.createdAt)}</td>
                  <td>{e.kind === 'status' ? `${e.fromStatus} → ${e.toStatus}` : e.kind}</td>
                  <td>
                    {e.actorName ?? '—'} <span className="muted">({e.actorParticipantName ?? 'CER'})</span>
                  </td>
                  <td style={{ whiteSpace: 'pre-wrap' }}>{e.note}</td>
                  <td>{e.evidenceRef}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {canNote && (
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                addNote.mutate();
              }}
            >
              <Field label="Note">
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} disabled={change.isPending || addNote.isPending} />
              </Field>
              <Field label="Evidence reference" hint="Job sheet, photo or certificate reference">
                <input value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} disabled={change.isPending || addNote.isPending} />
              </Field>
              <ProblemAlert error={change.error ?? addNote.error} />
              <div className="row">
                <button type="submit" disabled={addNote.isPending || note.trim().length === 0}>
                  Add note
                </button>
                {participantNext && (
                  <button type="button" onClick={() => change.mutate(participantNext)} disabled={change.isPending || note.trim().length < 3}>
                    Mark {participantNext}
                  </button>
                )}
                {cerCanAct && (
                  <>
                    <select value={cerStatus} onChange={(e) => setCerStatus(e.target.value as typeof cerStatus)} aria-label="CER unit status">
                      {u.status !== 'verified' && <option value="verified">verified</option>}
                      <option value="closed">closed</option>
                    </select>
                    <button type="button" onClick={() => change.mutate(cerStatus)} disabled={change.isPending || note.trim().length < 3}>
                      Set unit {cerStatus}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}
